/**
 * A2UI Chart — Apache ECharts host.
 *
 * WHY ECharts (vs hand-rolled SVG): the ECharts `option` JSON is one of
 * the most widely known declarative chart schemas in LLM training data —
 * models emit it fluently with ZERO prompt teaching, and it covers dozens
 * of chart types (bar/line/pie/scatter/radar/heatmap/gauge/funnel/…) at
 * no additional prompt cost.
 *
 * Guardrails (catalog security story stays intact):
 *   • lazy `import('echarts')` — the app bundle pays nothing until a
 *     chart actually renders in chat
 *   • the option arrives as JSON so it CANNOT carry functions; we
 *     JSON-round-trip anyway (defense in depth) and FORCE tooltip
 *     renderMode 'richText' — ECharts' default tooltips are HTML DOM,
 *     an injection surface for hostile payloads. Canvas renderer only.
 *   • themed via an init-time theme built from the surrounding CSS
 *     variables (transparent bg, muted axis text, app palette first)
 */
import { useEffect, useMemo, useRef, useState } from 'react'

/** App palette first (indigo accent, keybook gold, then distinct hues). */
const CHART_PALETTE = ['#6366f1', '#d4a843', '#2dd4bf', '#f472b6', '#60a5fa', '#a78bfa', '#fb923c', '#4ade80']

const MIN_H = 160
const MAX_H = 420
const DEFAULT_H = 240

/**
 * Validate + neutralize an untrusted ECharts option.
 * Returns null when unusable (callers fail soft).
 */
export function sanitizeChartOption(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  let opt: Record<string, unknown>
  try {
    // JSON round-trip: strips functions/symbols/undefined — the payload
    // should already be pure JSON, this guarantees it.
    opt = JSON.parse(JSON.stringify(raw))
  } catch {
    return null
  }
  if (!opt.series) return null

  // Force non-HTML tooltips (default ECharts tooltips are HTML DOM).
  const forceTooltip = (t: unknown): Record<string, unknown> => ({
    ...(t && typeof t === 'object' && !Array.isArray(t) ? t as Record<string, unknown> : {}),
    renderMode: 'richText',
    confine: true,
    // formatter strings could still template weirdness — richText mode
    // renders them on canvas, so they're inert text either way.
  })
  opt.tooltip = Array.isArray(opt.tooltip) ? opt.tooltip.map(forceTooltip) : forceTooltip(opt.tooltip)
  opt.backgroundColor = 'transparent'
  return opt
}

/** ECharts theme derived from the surrounding CSS variables, so charts
 *  follow dark/dim/light automatically. Read from the HOST element (the
 *  vars live on the .theme-* ancestor, not :root). */
function buildTheme(el: HTMLElement) {
  const css = getComputedStyle(el)
  const v = (name: string, fallback: string) => css.getPropertyValue(name).trim() || fallback
  const muted = v('--text-muted', '#7f7f8a')
  const border = v('--border-primary', '#27272a')
  const text = v('--text-secondary', '#a1a1aa')
  // Attached labels (pie outside labels etc.) IGNORE textStyle.color —
  // zrender auto-colors them from its dark-mode flag, which it infers
  // from the chart background. Ours is forced transparent (≙ light), so
  // without this the labels render near-black + contrast stroke on our
  // dark surfaces. Light computed text color ⇒ dark surface.
  const rgb = css.color.match(/\d+/g)
  const darkMode = rgb ? (0.299 * +rgb[0] + 0.587 * +rgb[1] + 0.114 * +rgb[2]) / 255 > 0.5 : true
  const axis = {
    axisLine: { lineStyle: { color: border } },
    axisTick: { lineStyle: { color: border } },
    axisLabel: { color: muted },
    splitLine: { lineStyle: { color: border } },
    nameTextStyle: { color: muted },
  }
  return {
    darkMode,
    color: CHART_PALETTE,
    backgroundColor: 'transparent',
    textStyle: { color: text },
    title: { textStyle: { color: text, fontSize: 13 }, subtextStyle: { color: muted } },
    legend: { textStyle: { color: muted } },
    categoryAxis: axis,
    valueAxis: axis,
    timeAxis: axis,
    logAxis: axis,
  }
}

/** Minimal chart handle — avoids importing echarts types eagerly. */
interface ChartHandle { setOption: (o: unknown) => void; resize: () => void; dispose: () => void }

interface Props {
  option: unknown
  /** px, clamped [160, 420]; default 240 */
  height?: number
}

export function Chart({ option, height }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const [failed, setFailed] = useState(false)
  const sanitized = useMemo(() => sanitizeChartOption(option), [option])
  const h = Math.max(MIN_H, Math.min(Number(height) || DEFAULT_H, MAX_H))

  useEffect(() => {
    if (!sanitized || !ref.current) return
    let disposed = false
    let chart: ChartHandle | null = null
    let ro: ResizeObserver | null = null
    ;(async () => {
      try {
        const echarts = await import('echarts')
        if (disposed || !ref.current) return
        chart = echarts.init(ref.current, buildTheme(ref.current), { renderer: 'canvas' }) as unknown as ChartHandle
        chart.setOption(sanitized)
        ro = new ResizeObserver(() => { try { chart?.resize() } catch { /* disposed */ } })
        ro.observe(ref.current)
      } catch {
        // Bad option shapes throw inside setOption; a failed dynamic
        // import lands here too. Fail soft, never take down the surface.
        if (!disposed) setFailed(true)
      }
    })()
    return () => {
      disposed = true
      ro?.disconnect()
      try { chart?.dispose() } catch { /* already disposed */ }
    }
  }, [sanitized])

  if (!sanitized || failed) {
    return <span className="text-xs themed-text-muted italic">chart unavailable</span>
  }
  return <div ref={ref} data-testid="a2ui-chart" className="w-full my-1" style={{ height: h }} />
}
